import i18next from 'i18next'
import {register} from 'dna-react-ioc'
import {SettlementsStore} from '@/stores/settlements/SettlementsStore'
import {TSettlementsStore} from '~/code/pages/Settlements/PerTransactionDate/TSettlementsStore'
import {
    SettlementsFiltersStoreSymbol,
    SettlementsPaginationStoreSymbol,
    SettlementsPerDayPaginationStoreSymbol,
    SettlementsStoreSymbol
} from '~/code/pages/Settlements/PerTransactionDate/Settlements'
import {SettlementsFilterRangePickerContainerStoreSymbol} from '~/code/pages/Settlements/PerTransactionDate/components/SettlementsRangePicker'
import {
    AuthStoreSymbol,
    PaginationStore,
    PeriodType,
    RangePickerStore,
    SelectContainerStore
} from '@/stores'
import {
    IFilterContainerStore,
    PaginationContainerStoreType,
    TRangePickerContainerStore,
    TSelectContainerStore,
    TTransactionsCardWithBarChartContainerStore,
    TTransactionSummaryContainerStore
} from '@/components'
import {Settlement} from '@/stores/settlements/models/Settlement'
import {SettlementsFiltersStore} from '@/stores/settlements/stores'

import {interfaces} from 'inversify'
import {PerSettlementDateTransactionsHistoryDataStore} from '~/code/stores/settlements/PerSettlementDateTransactionsHistoryDataStore'
import {
    ITransactionsHistoryDataStore,
    PerSettlementDateCurrencySelectStoreSymbol,
    PerSettlementDateRangePickerStoreSymbol,
    PerSettlementDateTransactionsCardWithBarChartContainerStoreSymbol,
    PerSettlementDateTransactionsHistoryDataStoreSymbol,
    PerSettlementDateTransactionsHistoryPaginationStoreSymbol,
    PerSettlementDateTransactionSummaryStoreSymbol
} from '~/code/pages/Settlements'
import {SettlementsTransactionsCardWithBarChartContainerStore} from '~/code/stores/reports/SettlementsTransactionsCardWithBarChartContianerStore/SettlementsTransactionsCardWithBarChartContainerStore'
import {SettlementsTransactionSummaryStore} from '~/code/stores/reports/SettlementsTransactionSummaryStore/SettlementsTransactionSummaryStore'
import {DEFAULT_CURRENCY} from '@/constants/currencies'

export const settlementsState = () => {
    register<TSettlementsStore<Settlement>>(SettlementsStoreSymbol)
        .to(SettlementsStore)
        .inSingletonScope()

    register<IFilterContainerStore>(SettlementsFiltersStoreSymbol)
        .to(SettlementsFiltersStore)
        .inSingletonScope()

    register<PaginationContainerStoreType>(
        PerSettlementDateTransactionsHistoryPaginationStoreSymbol
    )
        .to(PaginationStore)
        .inSingletonScope()

    register<TRangePickerContainerStore>(SettlementsFilterRangePickerContainerStoreSymbol)
        .toDynamicValue(
            () =>
                new RangePickerStore({
                    period: PeriodType.Yesterday,
                    rangePresets: {
                        [i18next.t('RangePicker.presets.yesterday')]: PeriodType.Yesterday,
                        [i18next.t('RangePicker.presets.lastWeek')]: PeriodType.LastWeek,
                        [i18next.t('RangePicker.presets.lastMonth')]: PeriodType.LastMonth
                    }
                })
        )
        .inSingletonScope()

    register<PaginationContainerStoreType>(SettlementsPaginationStoreSymbol)
        .to(PaginationStore)
        .inSingletonScope()

    register<PaginationContainerStoreType>(SettlementsPerDayPaginationStoreSymbol)
        .to(PaginationStore)
        .inSingletonScope()

    register<TSelectContainerStore>(PerSettlementDateCurrencySelectStoreSymbol)
        .toDynamicValue(
            () =>
                new SelectContainerStore({
                    options: [DEFAULT_CURRENCY].map((item) => ({
                        label: item,
                        value: item
                    }))
                })
        )
        .inSingletonScope()

    register<TRangePickerContainerStore>(PerSettlementDateRangePickerStoreSymbol)
        .toDynamicValue(
            () =>
                new RangePickerStore({
                    period: PeriodType.Last7Days,
                    rangePresets: {
                        [i18next.t('RangePicker.presets.today')]: PeriodType.Day,
                        [i18next.t('RangePicker.presets.yesterday')]: PeriodType.Yesterday,
                        [i18next.t('RangePicker.presets.thisWeek')]: PeriodType.Week,
                        [i18next.t('RangePicker.presets.last7Days')]: PeriodType.Last7Days,
                        [i18next.t('RangePicker.presets.thisMonth')]: PeriodType.Month,
                        [i18next.t('RangePicker.presets.last30Days')]: PeriodType.Last30Days
                    }
        })
        )
        .inSingletonScope()

    register<ITransactionsHistoryDataStore>(PerSettlementDateTransactionsHistoryDataStoreSymbol)
        .toDynamicValue((context: interfaces.Context) => {
            const perSettlementsDateTransactionsHistoryDataStore =
                new PerSettlementDateTransactionsHistoryDataStore(
                    context.container.get(PerSettlementDateRangePickerStoreSymbol),
                    context.container.get(PerSettlementDateCurrencySelectStoreSymbol),
                    context.container.get(PerSettlementDateTransactionsHistoryPaginationStoreSymbol)
                )
            return perSettlementsDateTransactionsHistoryDataStore
        })
        .inSingletonScope()

    register<TTransactionSummaryContainerStore>(PerSettlementDateTransactionSummaryStoreSymbol)
        .toDynamicValue(
            (context: interfaces.Context) =>
                new SettlementsTransactionSummaryStore(
                    'settlements.PerSettlementDateTransactionSummaryStore',
                    context.container.get(PerSettlementDateRangePickerStoreSymbol),
                    context.container.get(PerSettlementDateCurrencySelectStoreSymbol)
                )
        )
        .inSingletonScope()

    register<TTransactionsCardWithBarChartContainerStore>(
        PerSettlementDateTransactionsCardWithBarChartContainerStoreSymbol
    )
        .toDynamicValue((context: interfaces.Context) => {
            const perSettlementDateTransactionsComparisonCardWithBarChartStore =
                new SettlementsTransactionsCardWithBarChartContainerStore(
                    context.container.get(AuthStoreSymbol),
                    context.container.get(PerSettlementDateRangePickerStoreSymbol),
                    context.container.get(PerSettlementDateCurrencySelectStoreSymbol)
                )

            return perSettlementDateTransactionsComparisonCardWithBarChartStore
        })
        .inSingletonScope()
}
